import { useMemo } from 'react';
import { SponsorPurchase } from '@vote4dance/judging-schema';
import { ClassId, CompetitionId } from '@vote4dance/judging-types';
import { SponsorItemClass, SponsorItemClassesSource, SponsorPurchasesSource } from '../models/sponsors';
import { compareSponsorPurchases, normalizeExternalUrl, normalizeSponsorSlotIds } from '../helpers/sponsors';
import { Stream } from './streams';

export type SponsorDisplayEntry = {
    purchase: SponsorPurchase;
    slotId: string;
    classIds: string[];
    sponsorTitle?: string;
    sponsorName: string;
    sponsorLink?: string;
    sponsorImageId?: string;
};

type UseSponsorDisplayData = {
    purchases: SponsorPurchase[];
    loading: boolean;
    error: any;
    sponsorsBySlotId: Map<string, SponsorDisplayEntry[]>;
};

type UseSponsorsOptions = {
    competitionId?: CompetitionId;
    slotId?: string;
    classId?: ClassId;
    limit?: number;
};

export const useSponsorDisplayData = (competitionId?: CompetitionId): UseSponsorDisplayData => {
    const [purchases, loadingPurchases, purchasesError] = Stream<SponsorPurchase[]>(
        competitionId ? SponsorPurchasesSource(competitionId) : undefined
    );
    const [itemClasses] = Stream<SponsorItemClass[]>(
        competitionId ? SponsorItemClassesSource(competitionId) : undefined
    );

    const classIdsBySponsorItemId = useMemo(() => {
        const next = new Map<string, string[]>();

        (itemClasses || []).forEach((itemClass) => {
            const sponsorItemId = String(itemClass.sponsor_item_id || '');
            const classId = String(itemClass.class_id || '');
            if (!sponsorItemId || !classId) {
                return;
            }

            if (!next.has(sponsorItemId)) {
                next.set(sponsorItemId, []);
            }

            next.get(sponsorItemId)!.push(classId);
        });

        return next;
    }, [itemClasses]);

    const sponsorsBySlotId = useMemo(() => {
        const next = new Map<string, SponsorDisplayEntry[]>();

        (purchases || []).forEach((purchase) => {
            if (Number((purchase as any)?.sponsor_item_active) !== 1) {
                return;
            }

            const sponsorName = String(purchase?.sponsor_purchase_company_name || '').trim();
            if (!sponsorName) {
                return;
            }

            const slotIds = normalizeSponsorSlotIds((purchase as any)?.sponsor_item_placements);
            if (slotIds.length === 0) {
                return;
            }

            slotIds.forEach((slotId) => {
                if (!next.has(slotId)) {
                    next.set(slotId, []);
                }

                next.get(slotId)!.push({
                    purchase,
                    slotId,
                    classIds: classIdsBySponsorItemId.get(String(purchase.sponsor_item_id || '')) || [],
                    sponsorTitle: String((purchase as any)?.sponsor_item_title || '').trim() || undefined,
                    sponsorName,
                    sponsorLink: normalizeExternalUrl(purchase.sponsor_purchase_company_link),
                    sponsorImageId: purchase.sponsor_purchase_company_image_id || undefined
                });
            });
        });

        next.forEach((list) => list.sort((a, b) => compareSponsorPurchases(a.purchase, b.purchase)));
        return next;
    }, [classIdsBySponsorItemId, purchases]);

    return {
        purchases: purchases || [],
        loading: loadingPurchases,
        error: purchasesError,
        sponsorsBySlotId
    };
};

export const useSponsors = ({
    competitionId,
    slotId,
    classId,
    limit
}: UseSponsorsOptions = {}) => {
    const data = useSponsorDisplayData(competitionId);

    const sponsors = useMemo(() => {
        const sponsorsForSlot = slotId
            ? (data.sponsorsBySlotId.get(slotId) || [])
            : Array.from(data.sponsorsBySlotId.values()).flat();

        const sponsorsForClass = classId
            ? sponsorsForSlot.filter((sponsor) => sponsor.classIds.includes(String(classId)))
            : sponsorsForSlot;

        if (typeof limit === 'number') {
            return sponsorsForClass.slice(0, limit);
        }

        return sponsorsForClass;
    }, [classId, data.sponsorsBySlotId, limit, slotId]);

    return {
        ...data,
        sponsor: sponsors[0],
        sponsors,
        shouldDisplay: sponsors.length > 0
    };
};
